import gql from "graphql-tag";

import type {GqlFetchFn} from "@khan/data-gql";
import {gqlOp, type GqlOperation} from "@khan/data-gql-core";
import {Errors, KAError} from "@khan/logging";

import {extractProgram} from "../internal/extractors.ts";

import type {
    projectsAuthoredByUser,
    projectsAuthoredByUserVariables,
} from "./__graphql-types__/projectsAuthoredByUser.ts";
import type {
    FetchParams,
    ProjectListFetcher,
    ProjectListResponse,
} from "./project-list-fetcher-types.ts";

const PROJECTS_AUTHORED_BY_USER_QUERY: GqlOperation<
    projectsAuthoredByUser,
    projectsAuthoredByUserVariables
> = gqlOp(gql`
    query projectsAuthoredByUser(
        $kaid: String
        $pageInfo: ListProgramsPageInfo
        $sort: ListProgramSortOrder
    ) {
        user(kaid: $kaid) {
            id
            programs(pageInfo: $pageInfo, sort: $sort) {
                complete
                cursor
                programs {
                    id
                    key
                    authorKaid
                    authorNickname
                    displayableSpinoffCount
                    imagePath
                    sumVotesIncremented
                    translatedTitle: title
                    url
                }
            }
        }
    }
`);

export type ConstructorParams = {
    gqlFetch: GqlFetchFn;
    // The KAID of the user whose projects to fetch. If undefined, the default
    // behavior is to fetch the current user's projects.
    kaid?: string;
    itemsPerPage: number;
};

export class ProjectsAuthoredByUserFetcher implements ProjectListFetcher {
    gqlFetch: GqlFetchFn;
    kaid: string | undefined;
    itemsPerPage: number;

    constructor(params: ConstructorParams) {
        this.gqlFetch = params.gqlFetch;
        this.kaid = params.kaid;
        this.itemsPerPage = params.itemsPerPage;
    }

    async fetch(params: FetchParams): Promise<ProjectListResponse> {
        const response = await this.gqlFetch(PROJECTS_AUTHORED_BY_USER_QUERY, {
            variables: {
                kaid: this.kaid,
                sort: params.sort,
                pageInfo: {
                    cursor: params.cursor,
                    itemsPerPage: this.itemsPerPage,
                },
            },
        });

        const page = throwIfNull(response.user?.programs);
        return {
            complete: page.complete,
            cursor: page.cursor,
            scratchpads: page.programs.map(extractProgram),
        };
    }

    fingerprint(): string {
        return "ProjectsAuthoredByUserFetcher " + (this.kaid ?? "undefined");
    }
}

function throwIfNull<T>(data?: T | null): T {
    if (data == null) {
        throw new KAError(
            "Received null data from the server",
            Errors.Internal,
        );
    }
    return data;
}
